import { createContext, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import type { Session, User } from '@supabase/supabase-js';
import { useQueryClient } from '@tanstack/react-query';
import { supabase, supabaseHarvest } from '@/lib/supabase';

/**
 * AdminStatus mirrors the harvest.admin_users row for the current user:
 *   - 'unknown'        — initial state, before the first lookup completes
 *   - 'authed-only'    — Supabase session exists but no admin_users row
 *   - 'viewer'/'admin' — admin_users.role for this user_id
 */
export type AdminStatus = 'unknown' | 'authed-only' | 'viewer' | 'admin';

interface AuthState {
  session: Session | null;
  user: User | null;
  loading: boolean;
  adminStatus: AdminStatus;
  /** True iff adminStatus is 'admin' OR 'viewer' (enrolled in admin_users). */
  isAdminMember: boolean;
  /** True iff adminStatus is 'admin' (write tier; harvest.is_admin() in canonical migration). */
  isAdmin: boolean;
  signInWithPassword: (email: string, password: string) => Promise<{ error: string | null }>;
  signOut: () => Promise<void>;
}

const AuthContext = createContext<AuthState | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const queryClient = useQueryClient();
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);
  const [adminStatus, setAdminStatus] = useState<AdminStatus>('unknown');

  useEffect(() => {
    let mounted = true;
    supabase.auth.getSession().then(({ data }) => {
      if (!mounted) return;
      setSession(data.session);
      setLoading(false);
    });
    const { data: sub } = supabase.auth.onAuthStateChange((_event, next) => {
      setSession(next);
    });
    return () => {
      mounted = false;
      sub.subscription.unsubscribe();
    };
  }, []);

  // Whenever the session changes, refresh adminStatus by querying
  // harvest.admin_users. The query goes through supabaseHarvest's custom
  // fetch, which attaches the bearer token from the default client. Per
  // PR 144's RLS, an authenticated user can SELECT their own admin_users
  // row when they are enrolled.
  useEffect(() => {
    if (!session?.user) {
      setAdminStatus(session ? 'authed-only' : 'unknown');
      return;
    }
    let cancelled = false;
    void (async () => {
      const { data, error } = await supabaseHarvest
        .from('admin_users')
        .select('role')
        .eq('user_id', session.user.id)
        .maybeSingle();
      if (cancelled) return;
      if (error) {
        // Any genuine error (network, permission) drops us into the
        // safest "authed-only" state — the dashboard will show the
        // explicit gate rather than a half-broken page.
        setAdminStatus('authed-only');
        return;
      }
      const role = (data as { role?: string } | null)?.role;
      if (role === 'admin') setAdminStatus('admin');
      else if (role === 'viewer') setAdminStatus('viewer');
      else setAdminStatus('authed-only');
    })();
    return () => {
      cancelled = true;
    };
  }, [session]);

  const value = useMemo<AuthState>(
    () => ({
      session,
      user: session?.user ?? null,
      loading,
      adminStatus,
      isAdminMember: adminStatus === 'admin' || adminStatus === 'viewer',
      isAdmin: adminStatus === 'admin',
      async signInWithPassword(email, password) {
        const { error } = await supabase.auth.signInWithPassword({ email, password });
        return { error: error?.message ?? null };
      },
      async signOut() {
        await supabase.auth.signOut();
        // Drop every cached query so a subsequent sign-in (potentially as
        // a different admin in the same tab) cannot briefly see the
        // previous user's data while React Query refetches.
        queryClient.clear();
      },
    }),
    [session, loading, adminStatus, queryClient],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthState {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used inside AuthProvider');
  return ctx;
}
